import type { LinkPreviewData } from '@goodchat/shared/types';
import { API_BASE_URL } from '@/lib/api';

function getProxiedImageUrl(imageUrl: string): string {
  return `${API_BASE_URL}/proxy/image?url=${encodeURIComponent(imageUrl)}`;
}

interface LinkPreviewCardProps {
  preview: LinkPreviewData;
  isAuthor?: boolean;
  onHide?: () => void;
}

export function LinkPreviewCard({ preview, isAuthor, onHide }: LinkPreviewCardProps) {
  if (!preview.title && !preview.description) return null;

  return (
    <a
      href={preview.url}
      target="_blank"
      rel="noopener noreferrer"
      className="mt-1 flex gap-3 rounded-lg border border-gray-200 bg-gray-50 p-3 transition-[colors,shadow] duration-200 hover:bg-gray-100 hover:shadow-md dark:border-gray-700 dark:bg-gray-800 dark:hover:bg-gray-750"
      onClick={(e) => e.stopPropagation()}
    >
      {preview.imageUrl && (
        <img
          src={getProxiedImageUrl(preview.imageUrl)}
          alt=""
          className="h-16 w-16 flex-shrink-0 rounded object-cover"
          loading="lazy"
          decoding="async"
          onError={(e) => {
            (e.target as HTMLImageElement).style.display = 'none';
          }}
        />
      )}
      <div className="min-w-0 flex-1">
        {preview.title && (
          <p className="truncate text-sm font-medium text-gray-900 dark:text-gray-100">
            {preview.title}
          </p>
        )}
        {preview.description && (
          <p className="mt-0.5 line-clamp-2 text-xs text-gray-500 dark:text-gray-400">
            {preview.description}
          </p>
        )}
        <p className="mt-1 truncate text-xs text-gray-400 dark:text-gray-500">
          {new URL(preview.url).hostname}
        </p>
      </div>
      {isAuthor && onHide && (
        <button
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            onHide();
          }}
          className="flex-shrink-0 self-start rounded p-1 text-gray-400 hover:bg-gray-200 hover:text-gray-600 dark:hover:bg-gray-700 dark:hover:text-gray-300"
          title="Hide preview"
        >
          <svg className="h-3.5 w-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>
      )}
    </a>
  );
}
